import { useCallback } from "react"
import {
  DashboardConfig,
  DashboardConfigChange,
  Slide,
  Template,
} from "types/ReportingDashboard/reporting.types"

/**
 * Parameters for the useBackgroundColorManagement hook
 */
interface BackgroundColorManagementParams {
  /** Currently active slide */
  activeSlide: Slide | undefined
  /** ID of the currently active slide */
  activeSlideId: string | null
  /** Current slides array */
  currentSlides: Slide[]
  /** Current templates object */
  currentTemplates: Record<string, Template>
  /** Current dashboard configuration */
  dashboardConfig: DashboardConfig
  /** Whether a template is currently being edited */
  isEditingTemplate: boolean
  /** ID of the template being edited */
  editingTemplate: string | null
  /** Callback to update dashboard configuration */
  onDashboardConfigChange: (change: DashboardConfigChange) => void
}

/**
 * Return type for the useBackgroundColorManagement hook
 */
interface BackgroundColorManagementReturn {
  /** Function to get the current background color for the active slide/template */
  getCurrentBackgroundColor: () => string
  /** Function to handle background color changes for current slide or template */
  handleBackgroundColorChange: (color: string) => void
}

/**
 * Custom hook for managing background color operations in the reporting display.
 *
 * Handles:
 * - Getting current background color with fallback hierarchy
 * - Updating background colors for slides and templates
 * - Proper color inheritance from templates to slides
 *
 * Background Color Priority:
 * 1. Slide's own background color
 * 2. Template's background color (if slide uses a template)
 * 3. Dashboard default background color
 * 4. White (#ffffff) as final fallback
 *
 * @param params - Configuration parameters for background color management
 * @returns Object containing background color functions
 */
export const useBackgroundColorManagement = ({
  activeSlide,
  activeSlideId,
  currentSlides,
  currentTemplates,
  dashboardConfig,
  isEditingTemplate,
  editingTemplate,
  onDashboardConfigChange,
}: BackgroundColorManagementParams): BackgroundColorManagementReturn => {
  /**
   * Get the current background color for the active slide/template.
   * Uses a fallback hierarchy to ensure there's always a valid color.
   *
   * @function getCurrentBackgroundColor
   * @returns The current background color as a hex string
   */
  const getCurrentBackgroundColor = useCallback((): string => {
    if (activeSlide) {
      // First check if slide has its own background color
      if (activeSlide.backgroundColor) {
        return activeSlide.backgroundColor
      }

      // Then check if slide uses a template with a background color
      if (activeSlide.templateId && currentTemplates[activeSlide.templateId]) {
        const templateBgColor =
          currentTemplates[activeSlide.templateId].backgroundColor
        if (templateBgColor) {
          return templateBgColor
        }
      }
    }

    // Fall back to dashboard default or white
    return dashboardConfig.defaultBackgroundColor || "#ffffff"
  }, [activeSlide, currentTemplates, dashboardConfig.defaultBackgroundColor])

  /**
   * Handle background color change for current slide or template.
   * Updates the appropriate entity based on current editing context.
   *
   * @function handleBackgroundColorChange
   * @param color - The new background color as a hex string
   */
  const handleBackgroundColorChange = useCallback(
    (color: string): void => {
      if (isEditingTemplate && editingTemplate) {
        // Update template background color
        const updatedTemplates = {
          ...currentTemplates,
          [editingTemplate]: {
            ...currentTemplates[editingTemplate],
            backgroundColor: color,
          },
        }
        onDashboardConfigChange({
          slides: currentSlides,
          templates: updatedTemplates,
        })
      } else if (activeSlide) {
        // Update slide background color
        const updatedSlides = currentSlides.map((slide: Slide) =>
          slide.id === activeSlideId
            ? { ...slide, backgroundColor: color }
            : slide
        )
        onDashboardConfigChange({
          slides: updatedSlides,
          templates: currentTemplates,
        })
      }
    },
    [
      isEditingTemplate,
      editingTemplate,
      currentTemplates,
      activeSlide,
      activeSlideId,
      currentSlides,
      onDashboardConfigChange,
    ]
  )

  return {
    getCurrentBackgroundColor,
    handleBackgroundColorChange,
  }
}
